import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Globe, BookOpen, Compass } from "lucide-react";

const PILLAR_MOMENTS = [
  {
    pillar: "Never stumble on facts",
    source: "sourced from the web",
    sourceIcon: Globe,
    sourceColor: "text-blue-500 bg-blue-50",
    bubble: {
      speaker: "Jordan",
      text: "The European market is about 40% of global SaaS spend, right?",
    },
    card: {
      label: "FACT CHECK",
      dot: "bg-blue-500",
      accent: "border-l-blue-400",
      verdict: "Partial",
      verdictColor: "text-amber-600",
      response: "32.4% per Statista 2025. North America leads at 52%.",
    },
  },
  {
    pillar: "Your docs, always at hand",
    source: "from your documents",
    sourceIcon: BookOpen,
    sourceColor: "text-teal-600 bg-teal-50",
    bubble: {
      speaker: "You",
      text: "Based on Last Proposal",
      isTriggerLabel: true,
    },
    card: {
      label: "GROUNDED",
      dot: "bg-teal-500",
      accent: "border-l-teal-400",
      response: "From your proposal: Year 1 target was 500 users at $45/seat, 20% annual growth.",
      sourceChip: "Q4 Proposal.pdf",
    },
  },
  {
    pillar: "Never miss what matters",
    source: "agenda tracking",
    sourceIcon: Compass,
    sourceColor: "text-red-500 bg-red-50",
    bubble: null,
    card: {
      label: "AGENDA",
      dot: "bg-red-500",
      accent: "border-l-red-400",
      response: "You haven't discussed pricing or budget yet. 5 minutes remaining.",
    },
  },
];

const PHASE_TIMING = {
  pillarDelay: 300,
  bubbleDelay: 800,
  thinkingDelay: 1500,
  cardDelay: 2400,
  holdDuration: 5500,
};

export default function HeroDemo({ variant = "default" }) {
  const v1 = variant === "v1";
  const accentText = v1 ? "text-[#B55238]" : "text-primary";
  const accentBg = v1 ? "bg-[#B55238]" : "bg-primary";
  const accentBgSoft = v1 ? "bg-[#B55238]/40" : "bg-primary/40";
  const [momentIndex, setMomentIndex] = useState(0);
  const [phase, setPhase] = useState("pillar");

  const advanceMoment = useCallback(() => {
    setPhase("pillar");
    setMomentIndex((prev) => (prev + 1) % PILLAR_MOMENTS.length);
  }, []);

  useEffect(() => {
    const timers = [];
    timers.push(setTimeout(() => setPhase("bubble"), PHASE_TIMING.bubbleDelay));
    timers.push(setTimeout(() => setPhase("thinking"), PHASE_TIMING.thinkingDelay));
    timers.push(setTimeout(() => setPhase("card"), PHASE_TIMING.cardDelay));
    timers.push(setTimeout(advanceMoment, PHASE_TIMING.holdDuration));
    return () => timers.forEach(clearTimeout);
  }, [momentIndex, advanceMoment]);

  const moment = PILLAR_MOMENTS[momentIndex];
  const SourceIcon = moment.sourceIcon;

  return (
    <div className="relative w-full max-w-lg mx-auto">
      <div className="relative p-4 sm:p-6">
        {/* Meeting header */}
        <div className="bg-white/80 backdrop-blur-sm rounded-xl border border-gray-200 p-3 sm:p-4 mb-4 shadow-sm">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            <span className="text-xs font-medium text-gray-600">Strategy Call</span>
            <span className="text-[10px] text-gray-400 ml-auto">Live</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex -space-x-2">
              {["Y", "J"].map((initial, i) => (
                <div
                  key={i}
                  className={`flex h-6 w-6 items-center justify-center rounded-full border-2 border-white text-[10px] font-semibold ${
                    i === 0 ? `${accentBg} text-white` : "bg-gray-200 text-gray-600"
                  }`}
                >
                  {initial}
                </div>
              ))}
            </div>
            <span className="text-[11px] text-gray-500">You + Jordan (Client)</span>
          </div>
        </div>

        {/* Pillar label */}
        <AnimatePresence mode="wait">
          <motion.div
            key={`pillar-${momentIndex}`}
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="mb-3"
          >
            <span className={`text-xs font-semibold uppercase tracking-wider ${accentText}`}>
              {moment.pillar}
            </span>
          </motion.div>
        </AnimatePresence>

        {/* Content area */}
        <div className="min-h-[220px] space-y-3">
          {/* Chat bubble */}
          <AnimatePresence mode="wait">
            {(phase === "bubble" || phase === "thinking" || phase === "card") && moment.bubble && (
              <motion.div
                key={`bubble-${momentIndex}`}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ type: "spring", stiffness: 400, damping: 30 }}
                className="py-1 px-1"
              >
                {moment.bubble.isTriggerLabel ? (
                  <>
                    <span className="text-[11px] font-medium text-gray-400 uppercase tracking-wide block mb-1">
                      {moment.bubble.speaker}
                    </span>
                    <p className="text-base text-gray-500 italic leading-snug">
                      {moment.bubble.text}
                    </p>
                  </>
                ) : (
                  <>
                    <span className="text-[11px] font-medium text-gray-400 uppercase tracking-wide block mb-1">
                      {moment.bubble.speaker}
                    </span>
                    <p className="text-base text-gray-500 leading-snug italic">
                      &quot;{moment.bubble.text}&quot;
                    </p>
                  </>
                )}
              </motion.div>
            )}

            {/* Agenda nudge has no bubble — show a subtle indicator instead */}
            {(phase === "bubble" || phase === "thinking" || phase === "card") && !moment.bubble && (
              <motion.div
                key={`no-bubble-${momentIndex}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex items-center gap-2 px-1 py-2"
              >
                <div className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                <span className="text-xs text-gray-500 italic">Meeting in progress... time is running low</span>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Thinking indicator */}
          <AnimatePresence>
            {phase === "thinking" && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex items-center gap-2 px-1"
              >
                <div className="flex gap-1">
                  {[0, 1, 2].map((i) => (
                    <motion.div
                      key={i}
                      className={`h-1.5 w-1.5 rounded-full ${accentBgSoft}`}
                      animate={{ scale: [1, 1.4, 1] }}
                      transition={{ duration: 0.6, repeat: Infinity, delay: i * 0.15 }}
                    />
                  ))}
                </div>
                <span className="text-[11px] text-gray-400">Shadow is thinking...</span>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Shadow card */}
          <AnimatePresence>
            {phase === "card" && (
              <motion.div
                key={`card-${momentIndex}`}
                initial={{ opacity: 0, y: 12, scale: 0.97 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ type: "spring", stiffness: 400, damping: 30 }}
                className={`bg-white border border-gray-200 border-l-[3px] ${moment.card.accent} px-4 py-3.5 shadow-sm`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <span className={`w-1.5 h-1.5 rounded-full ${moment.card.dot}`} />
                  <span className="text-[10px] font-medium text-gray-500 uppercase tracking-wide">
                    {moment.card.label}
                  </span>
                  {moment.card.verdict && (
                    <>
                      <span className="text-gray-300">·</span>
                      <span className={`text-[10px] font-semibold ${moment.card.verdictColor}`}>
                        {moment.card.verdict}
                      </span>
                    </>
                  )}
                </div>
                <p className="text-sm leading-snug text-gray-900 mb-2">
                  &quot;{moment.card.response}&quot;
                </p>
                <div className="flex items-center gap-1.5">
                  {moment.card.sourceChip ? (
                    <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-md bg-teal-500/10 text-teal-700 font-medium">
                      <BookOpen className="w-2.5 h-2.5" />
                      {moment.card.sourceChip}
                    </span>
                  ) : (
                    <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-md ${moment.sourceColor} font-medium`}>
                      <SourceIcon className="w-2.5 h-2.5" />
                      {moment.source}
                    </span>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Progress dots */}
        <div className="flex justify-center gap-2 mt-4">
          {PILLAR_MOMENTS.map((_, i) => (
            <div
              key={i}
              className={`h-1.5 rounded-full transition-all duration-500 ${
                i === momentIndex ? `w-6 ${accentBg}` : "w-1.5 bg-gray-200"
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
